import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { c } from 'ttag';

import {
    AuthenticationStore,
    Button,
    ChallengeName,
    InputField,
    PasswordInput,
    useAuthentication,
    useFormErrors,
    useLoading,
    useNotifications,
} from '@freelancelabs/components';
import { requiredValidator } from '@freelancelabs/shared';

import { findSsoProvider } from './ssoDiscovery';

interface Props {
    onSubmit: (data: {
        challenge: ChallengeName | undefined;
        payload: {
            username: string;
            password: string;
        };
    }) => void;
    defaultUsername?: string;
    // SSO mode reuses the same form so the email field keeps its value across the toggle.
    ssoMode?: boolean;
    onUsernameChange?: (username: string) => void;
}

const LoginForm = ({ defaultUsername = '', onSubmit, ssoMode = false, onUsernameChange }: Props) => {
    const { login, signInWithSso } = useAuthentication() as AuthenticationStore;
    const { validator, onFormSubmit } = useFormErrors();
    const [submitting, withSubmitting] = useLoading();
    const [username, setUsername] = useState(defaultUsername || '');
    const [password, setPassword] = useState('');
    // The SSO refusal sits under the field (not a notification) because it is about what was just typed.
    const [ssoRefusal, setSsoRefusal] = useState<string | null>(null);

    const { createNotification } = useNotifications();

    // Must be a variable, not a function call, inside the jt template: babel-plugin-ttag
    // refuses a call at extraction. The key avoids React's list warning (jt renders an array).
    const resetPasswordLink = (
        <Link key="reset-password" to="/reset-password" className="link font-medium">
            {c('Link').t`password`}
        </Link>
    );

    const submitSso = () => {
        setSsoRefusal(null);
        onUsernameChange?.(username);

        const run = async () => {
            const provider = await findSsoProvider(username);
            if (!provider) {
                setSsoRefusal(
                    c('Sso').t`No single sign-on is set up for this email domain. Sign in with your password instead.`
                );
                return;
            }
            // The redirect replaces the page, so the button stays loading until the browser leaves.
            await signInWithSso(provider);
        };

        withSubmitting(run()).catch(() => {
            // Only reachable failure: OAuth is not mounted on this app, which signInWithSso throws.
            setSsoRefusal(c('Sso').t`Single sign-on is unavailable right now. Sign in with your password instead.`);
        });
    };

    const submitPassword = () => {
        const run = async () => {
            const challenge = await login({ email: username, password });
            return onSubmit({
                challenge,
                payload: {
                    username,
                    password,
                },
            });
        };

        withSubmitting(run()).catch((e) => {
            const message = e.message;
            switch (message) {
                case 'Incorrect username or password.':
                    createNotification({
                        type: 'danger',
                        text: c('Login Error').t`Incorrect username or password.`,
                    });
                    break;
                case 'Password attempts exceeded':
                    createNotification({
                        type: 'danger',
                        text: c('Login Error').t`Password attempts exceeded`,
                    });
                    break;
                default:
                    createNotification({
                        type: 'danger',
                        text: c('Login Error').t`Unknown error`,
                    });
            }
        });
    };

    const handleSubmit = () => {
        if (submitting || !onFormSubmit()) {
            return;
        }
        if (ssoMode) {
            submitSso();
            return;
        }
        submitPassword();
    };

    return (
        <form
            name="loginForm"
            className="flex flex-col"
            onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
            }}
            method="post"
        >
            <InputField
                label={c('Info').t`Email address`}
                id="username"
                type="email"
                error={validator([requiredValidator(username)])}
                disableChange={submitting}
                autoComplete="username"
                value={username}
                onValue={(value: string) => {
                    setUsername(value);
                    // Clear the refusal as the address changes, since it was about that address.
                    setSsoRefusal(null);
                }}
            />
            {/* Password and its link leave the DOM in SSO mode rather than being disabled. */}
            {!ssoMode && (
                <>
                    <InputField
                        label={c('Info').t`Password`}
                        id="password"
                        as={PasswordInput}
                        error={validator([requiredValidator(password)])}
                        disableChange={submitting}
                        autoComplete="current-password"
                        value={password}
                        onValue={setPassword}
                    />
                </>
            )}
            {ssoRefusal && (
                <p role="alert" className="text-xs text-danger-700 pb-2">
                    {ssoRefusal}
                </p>
            )}
            <Button size="large" type="submit" fullWidth loading={submitting} className="mt-4">
                {ssoMode ? c('Sso').t`Continue with SSO` : c('Action').t`Login`}
            </Button>
            {/* Absent in SSO mode: no password to forget. */}
            {!ssoMode && (
                <p className="text-sm text-text-weak text-center mt-4">
                    {c('Login').jt`Forgot your ${resetPasswordLink}?`}
                </p>
            )}
        </form>
    );
};

export default LoginForm;
